import * as React from 'react'
import { ActionBarDropdown } from '@/features/action-bar/items/action-bar-dropdown/action-bar-dropdown'
import { MobileHeaderRenderedByDialogContext, MOBILE_FULLSCREEN_HEADER_ROW_CLASS } from '@/components/ui/dialog'
import { UrlConfig } from '@/setup/routing/routing'
import { cn } from '@/utils/styles/cn'
import { IClassNameProps } from '@/utils/typings/class-name-props'
import { useUser } from '@clerk/nextjs'
import { AnimatePresence, motion } from 'framer-motion'
import { usePathname } from 'next/navigation'
import { SuperscaleLogoIcon } from '@/components/icons/superscale-logo-icon'

export type IStepHeaderActionBarMode = 'default' | 'mobile-only' | 'none'

const StepHeaderActionBarModeContext = React.createContext<IStepHeaderActionBarMode>('default')

export const StepHeaderActionBarModeProvider = ({
    value,
    children,
}: {
    value: IStepHeaderActionBarMode
    children: React.ReactNode
}) => <StepHeaderActionBarModeContext.Provider value={value}>{children}</StepHeaderActionBarModeContext.Provider>

export type IStepHeaderProps = IClassNameProps & {
    withoutBackground?: boolean
    title: string
    subtitle?: string
    titleClassName?: string
    /** Rendered on the left of the action bar on mobile (e.g. Back button). Action bar moves above title on mobile when present or when action bar is shown. */
    topBarLeft?: React.ReactNode
}

export const StepHeader = ({ title, subtitle, titleClassName, className, topBarLeft }: IStepHeaderProps) => {
    const { isSignedIn } = useUser()
    const pathname = usePathname()
    const actionBarMode = React.useContext(StepHeaderActionBarModeContext)

    const isAuthRoute = pathname.includes(UrlConfig['/auth'].getUrl())

    const mobileHeaderRenderedByDialog = React.useContext(MobileHeaderRenderedByDialogContext)
    const shouldDisplayActionBar = actionBarMode !== 'none' && isSignedIn && !isAuthRoute
    const showTopBarOnMobile =
        !mobileHeaderRenderedByDialog &&
        (actionBarMode === 'mobile-only' || Boolean(shouldDisplayActionBar || topBarLeft))

    return (
        <AnimatePresence initial={false}>
            <div
                className={cn(
                    'flex flex-col items-start md:items-center mb-4 max-md:px-0 md:px-4 max-w-[480px]',
                    shouldDisplayActionBar ? 'max-w-none' : '',
                    className
                )}
            >
                {/* On mobile: top bar row (back button + action bar) above title so they don't overlay */}
                {showTopBarOnMobile && (
                    <div className={MOBILE_FULLSCREEN_HEADER_ROW_CLASS}>
                        <div className="min-w-0 flex items-center">
                            {topBarLeft ?? (
                                <div className="flex items-center gap-1">
                                    <SuperscaleLogoIcon className="size-5" />
                                    <span className="text-sm font-semibold">Superscale</span>
                                </div>
                            )}
                        </div>
                        {shouldDisplayActionBar && (
                            <div className="shrink-0">
                                <ActionBarDropdown hideChevron />
                            </div>
                        )}
                    </div>
                )}

                <div className="relative w-full">
                    <motion.h2
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        key={title}
                        className={cn(
                            'text-xl font-semibold text-left md:text-center wrap-break-word whitespace-normal mb-2',
                            titleClassName
                        )}
                    >
                        {title}
                    </motion.h2>

                    {/* On desktop: action bar stays absolute over title area */}
                    {actionBarMode === 'default' && shouldDisplayActionBar && (
                        <div className="absolute right-0 top-0 max-md:hidden">
                            <ActionBarDropdown
                                config={{
                                    billing: false,
                                    credits: false,
                                    inviteTeam: false,
                                    organizationSwitcher: true,
                                    settings: false,
                                    roadmap: false,
                                    signOut: true,
                                }}
                                hideChevron
                            />
                        </div>
                    )}
                </div>

                {subtitle && (
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        key={subtitle}
                        className="text text-[#474747CC] text-left md:text-center wrap-break-word whitespace-normal"
                    >
                        {subtitle}
                    </motion.p>
                )}
            </div>
        </AnimatePresence>
    )
}
